import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles
}) => {
  const { user, isLoading, userRole } = useAuth();
  const location = useLocation();
  const [onboardingStatus, setOnboardingStatus] = useState<boolean | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const loadingTimeoutRef = useRef<NodeJS.Timeout>();
  const mountedRef = useRef(true);

  // Add cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Check onboarding status with better error handling and caching
  useEffect(() => {
    let isMounted = true;
    const cachedStatus = localStorage.getItem(`onboarding-${user?.id}`);

    async function checkOnboardingStatus() {
      if (!user || userRole === 'admin') {
        if (isMounted) {
          setCheckingOnboarding(false);
        }
        return;
      }

      try {
        // Always fetch from database to ensure accuracy
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', user.id)
          .single();

        if (error) throw error;

        if (isMounted) {
          const isCompleted = data.onboarding_completed;
          setOnboardingStatus(isCompleted);
          // Update cache with latest value
          localStorage.setItem(
            `onboarding-${user.id}`, 
            JSON.stringify(isCompleted)
          );
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        if (isMounted) {
          // If database check fails, fall back to cache
          if (cachedStatus) {
            setOnboardingStatus(JSON.parse(cachedStatus));
          } else {
            toast.error('Error checking user profile');
            setOnboardingStatus(false);
          }
        }
      } finally {
        if (isMounted) {
          setCheckingOnboarding(false);
        }
      }
    }

    checkOnboardingStatus();
    return () => { isMounted = false; };
  }, [user, userRole]);

  // Add timeout for loading state
  useEffect(() => {
    if (isLoading || checkingOnboarding) {
      loadingTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && (isLoading || checkingOnboarding)) {
          toast.error("Loading is taking longer than expected. Please refresh the page.");
        }
      }, 5000); // 5 seconds timeout

      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      };
    }
  }, [isLoading, checkingOnboarding]);

  // Show loading state while checking auth and onboarding
  if (isLoading || checkingOnboarding) {
    return (
      <div className="container mx-auto p-8">
        <div className="space-y-4" data-testid="loading-skeleton">
          <Skeleton className="h-12 w-full animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-[200px] w-full rounded-lg animate-pulse" />
            <Skeleton className="h-[200px] w-full rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // If no user is logged in, redirect to home with return path
  if (!user) {
    toast.error("Please log in to access this page");
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Handle onboarding route access with improved logic
  const isOnboardingRoute = location.pathname === '/onboarding';
  if (isOnboardingRoute) {
    if (userRole === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    
    if (onboardingStatus) {
      const redirectPath = userRole === 'entrepreneur' ? '/entrepreneur' : '/investor';
      return <Navigate to={redirectPath} replace />;
    }
    
    if (['entrepreneur', 'investor'].includes(userRole || '')) {
      return <>{children}</>;
    }
  }

  // For non-onboarding routes, check if onboarding is required
  if (!onboardingStatus && userRole !== 'admin' && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check if user's role is allowed for the requested route
  const hasAllowedRole = allowedRoles.includes(userRole || '');
  
  if (!hasAllowedRole) {
    toast.error("You don't have permission to access this page");
    return <Navigate to="/" replace />;
  }

  // User has required access, render children
  return <>{children}</>;
};

export default ProtectedRoute;
